import { useState, useEffect } from 'react';
import { useTheme } from './theme';

const PROXY = (import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001").replace(/\/+$/, "");

/**
 * AML Model Manager
 * Dashboard for managing, monitoring, and improving the quantitative model
 */
export function AMLModelManager({ companyId }) {
  const C = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calibrating, setCalibrating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadModelStats();
    const interval = setInterval(loadModelStats, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [companyId]);

  const loadModelStats = async () => {
    try {
      const token = localStorage.getItem('amlguard_token');
      if (!token) {
        console.warn('No auth token available for AML stats');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `/api/aml/stats?companyId=${companyId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        console.error(`[AML Stats] HTTP ${response.status}`);
        setLoading(false);
        return;
      }

      const data = await response.json();
      setStats(data.stats);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load model stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalibrate = async () => {
    setCalibrating(true);
    try {
      // Get recent clean transactions (analyst-marked as low risk)
      const response = await fetch(`${PROXY}/api/aml/calibrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          transactionIds: [], // Would need to pass actual IDs
        }),
      });

      if (response.ok) {
        alert('✓ Model calibrated successfully');
        await loadModelStats();
      }
    } catch (error) {
      alert('Failed to calibrate: ' + error.message);
    } finally {
      setCalibrating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: C.textDim }}>
        Loading model stats...
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: 20, color: C.textDim }}>
        No model data available
      </div>
    );
  }

  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 24,
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.text, marginBottom: 4 }}>📊 AML Model Dashboard</h2>
        <div style={{ fontSize: 12, color: C.textDim }}>
          Quantitative risk scoring engine • Last updated{' '}
          {lastUpdated?.toLocaleTimeString()}
        </div>
      </div>

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* Total Scored */}
        <StatCard
          label="Transactions Scored"
          value={stats.totalTransactionScored}
          color="#4CAF50"
          theme={C}
        />

        {/* Alerts */}
        <StatCard
          label="Alerts Generated"
          value={stats.alerts}
          subtext={`${((stats.alerts / (stats.totalTransactionScored || 1)) * 100).toFixed(1)}% alert rate`}
          color="#FF6B6B"
          theme={C}
        />

        {/* Flagged */}
        <StatCard
          label="Flagged for Review"
          value={stats.flagged}
          color="#FFA500"
          theme={C}
        />

        {/* Avg Risk */}
        <StatCard
          label="Average Risk Score"
          value={parseFloat(stats.avgRiskScore).toFixed(1)}
          color="#FFD700"
          theme={C}
        />

        {/* Avg Confidence */}
        <StatCard
          label="Model Confidence"
          value={parseFloat(stats.avgConfidence).toFixed(1) + '%'}
          color="#2196F3"
          theme={C}
        />

        {/* Model Precision */}
        <StatCard
          label="Model Precision"
          value={stats.modelPrecision}
          subtext={`${stats.truePositives} correct of ${stats.totalFeedback}`}
          color={stats.modelPrecision > 75 ? '#4CAF50' : '#FFD700'}
          theme={C}
        />
      </div>

      {/* Feedback Stats */}
      <div
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <h3 style={{ color: C.text, fontSize: 14, marginBottom: 12 }}>
          Learning Progress
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 6 }}>
              Total Feedback Samples
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: '#4CAF50',
                marginBottom: 8,
              }}
            >
              {stats.totalFeedback}
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.textDim,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}
            >
              <div>
                <span style={{ color: '#4CAF50', fontWeight: 600 }}>
                  {stats.truePositives}
                </span>{' '}
                True Positives
              </div>
              <div>
                <span style={{ color: '#FFA500', fontWeight: 600 }}>
                  {stats.falsePositives}
                </span>{' '}
                False Positives
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 6 }}>
              Feedback Impact
            </div>
            <div
              style={{
                height: 100,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* True Positive Bar */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: `${
                    stats.totalFeedback > 0
                      ? (stats.truePositives / stats.totalFeedback) * 100
                      : 0
                  }%`,
                  width: '100%',
                  background: '#4CAF50',
                  opacity: 0.7,
                }}
              />
              {/* False Positive Bar */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  height: `${
                    stats.totalFeedback > 0
                      ? (stats.falsePositives / stats.totalFeedback) * 100
                      : 0
                  }%`,
                  width: '100%',
                  background: '#FFA500',
                  opacity: 0.7,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.text,
                  textShadow: '0 0 4px rgba(0,0,0,0.5)',
                }}
              >
                {stats.modelPrecision}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Model Controls */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
        }}
      >
        <button
          onClick={handleCalibrate}
          disabled={calibrating}
          style={{
            background: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 600,
            cursor: calibrating ? 'not-allowed' : 'pointer',
            opacity: calibrating ? 0.6 : 1,
          }}
        >
          {calibrating ? '⏳ Calibrating...' : '🔧 Calibrate from Clean Data'}
        </button>

        <button
          style={{
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
          onClick={() => {
            alert(
              'Model Learning:\n\n' +
                '1. Each analyst feedback updates model weights\n' +
                '2. True positives reinforce risk features\n' +
                '3. False positives reduce false-positive patterns\n' +
                '4. Baseline calibrated from clean historical data\n\n' +
                'Current Precision: ' +
                stats.modelPrecision
            );
          }}
        >
          ℹ️ How Model Learns
        </button>
      </div>

      {/* Info */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${C.border}`,
          fontSize: 11,
          color: C.textDim,
          lineHeight: 1.6,
        }}
      >
        <strong>📈 Model Status:</strong> The quantitative AML model continuously learns
        from analyst feedback. Every time you mark a transaction as a true or false
        positive, the model adjusts its risk signals. Higher precision indicates the
        model is making accurate predictions.
      </div>
    </div>
  );
}

/**
 * Reusable stat card component
 */
function StatCard({ label, value, subtext, color, theme: C }) {
  return (
    <div
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: 16,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color, marginBottom: 4 }}>
        {value}
      </div>
      {subtext && (
        <div style={{ fontSize: 10, color: C.textDim }}>
          {subtext}
        </div>
      )}
    </div>
  );
}

export default AMLModelManager;
